import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import DatePicker from "react-datepicker";
import { AsyncPaginate } from "react-select-async-paginate";

import { fetchAccountBillingCycle, fetchBillRuns, getBillingCycles } from "../../utils/apiUtils";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import SharedTable from "../benefitPlan/SharedTable";
import {
    accountFromSearchParams,
    money,
    toIsoDate,
    writeAccountParams,
} from "./financeShared";
import "./BillRuns.scss";

const PAGE_SIZE = 40;

const DAY_NAMES = [
    "",
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const ordinal = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return null;
    const mod = num % 100;
    const suffix =
        mod >= 11 && mod <= 13 ? "th" : { 1: "st", 2: "nd", 3: "rd" }[num % 10] || "th";
    return `${num}${suffix}`;
};

const dayOfMonthLabel = (n, { startWord = "Start" } = {}) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return null;
    if (num === 1) return `${startWord} of the month`;
    return `${ordinal(num)} of the month`;
};

const scheduleLabel = (schedule) => {
    const s = String(schedule || "").trim();
    if (!s) return null;
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const cycleStatus = (code) => {
    const s = String(code || "").trim().toLowerCase();
    if (s === "a" || s === "active") return { label: "Active", tone: "ok" };
    if (s === "t" || s === "terminated") return { label: "Terminated", tone: "danger" };
    if (!s) return null;
    return { label: s.toUpperCase(), tone: "muted" };
};

const catalogRows = (payload) => {
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
};

const enrichCycle = (cycle, catalog) => {
    if (!cycle || cycle.errorMessage) return cycle;
    if (cycle.runDayOfMonth != null || cycle.periodStartDayOfMonth != null) return cycle;
    const rows = catalogRows(catalog);
    const extra = rows.find(
        (row) =>
            (cycle.billingCycleFactKey != null &&
                Number(row.billingCycleFactKey) === Number(cycle.billingCycleFactKey)) ||
            (cycle.billingCycleName && row.billingCycleName === cycle.billingCycleName),
    );
    if (!extra) return cycle;
    return {
        ...cycle,
        runDayOfMonth: cycle.runDayOfMonth ?? extra.runDayOfMonth,
        periodStartDayOfMonth: cycle.periodStartDayOfMonth ?? extra.periodStartDayOfMonth,
        runMonthsAhead: cycle.runMonthsAhead ?? extra.runMonthsAhead,
        dayOfWeek: cycle.dayOfWeek ?? extra.dayOfWeek,
        hourOfDay: cycle.hourOfDay ?? extra.hourOfDay,
        minuteOfHour: cycle.minuteOfHour ?? extra.minuteOfHour,
        daysBetDueDtAndPeriod: cycle.daysBetDueDtAndPeriod ?? extra.daysBetDueDtAndPeriod,
        makeUpForDayOff: cycle.makeUpForDayOff ?? extra.makeUpForDayOff,
    };
};

const BillRuns = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [account, setAccount] = useState(() => accountFromSearchParams(searchParams));
    const [asOfDate, setAsOfDate] = useState(() => new Date());
    const [search, setSearch] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [showMoreColumns, setShowMoreColumns] = useState(false);
    const { ref, inView } = useInView();
    const asOf = toIsoDate(asOfDate);
    const accountKey = account?.additional?.accountKey;
    const accountHccId = account?.additional?.accountHccId;

    useEffect(() => {
        const next = accountFromSearchParams(searchParams);
        if (next?.additional?.accountKey !== account?.additional?.accountKey) {
            setAccount(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("accountKey")]);

    const onAccountChange = (opt) => {
        setAccount(opt);
        setSearchParams(writeAccountParams(searchParams, opt), { replace: true });
    };

    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        error,
        status,
    } = useInfiniteQuery({
        queryKey: ["bill-runs", accountKey || null, asOf, searchApplied],
        queryFn: ({ pageParam = 0 }) =>
            fetchBillRuns({
                accountKey,
                asOfDate: asOf,
                q: searchApplied || undefined,
                limit: PAGE_SIZE,
                offset: pageParam,
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const loaded = pages.reduce((acc, p) => acc + (p?.items?.length || 0), 0);
            const total = lastPage?.total ?? loaded;
            if (loaded >= total || (lastPage?.items?.length || 0) < PAGE_SIZE) return undefined;
            return loaded;
        },
        staleTime: 30_000,
    });

    const {
        data: cycle,
        isLoading: cycleLoading,
        error: cycleError,
    } = useQuery({
        queryKey: ["account-billing-cycle", accountKey || null, accountHccId || null, asOf],
        queryFn: () =>
            fetchAccountBillingCycle({
                accountKey: accountKey || undefined,
                accountHccId: accountHccId || undefined,
                asOfDate: asOf,
            }),
        enabled: Boolean(accountKey || accountHccId),
        staleTime: 30_000,
        retry: false,
    });

    const { data: cycleCatalog } = useQuery({
        queryKey: ["billing-cycles-catalog"],
        queryFn: () => getBillingCycles({ limit: 50, offset: 0 }),
        enabled: Boolean(accountKey || accountHccId),
        staleTime: 5 * 60_000,
        retry: false,
    });

    const cycleDetails = useMemo(
        () => enrichCycle(cycle, cycleCatalog),
        [cycle, cycleCatalog],
    );

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const items = useMemo(
        () =>
            (data?.pages || []).flatMap((p) => p?.items || []).map((r) => ({
                ...r,
                billFactKey: r.key,
                statusLabel: r.statusLabel || r.status,
                totalDue: money(r.totalDue),
                totalPayments: money(r.totalPayments),
                totalCurrentCharges: money(r.totalCurrentCharges),
                totalAdjustments: money(r.totalAdjustments),
            })),
        [data],
    );

    let columns = [
        "billFactKey",
        "accountHccId",
        "accountName",
        "statusLabel",
        "paidStatus",
        "billingCycleName",
        "periodStart",
        "periodEnd",
        "dueDate",
        "totalDue",
        "totalPayments",
    ];
    if (showMoreColumns) {
        columns = [...columns, "totalCurrentCharges", "totalAdjustments", "isReconciled", "isVoid"];
    }

    const cycleChip = cycleStatus(cycleDetails?.billingCycleHistoryStatus);
    const showCycleDesc =
        Boolean(cycleDetails?.billingCycleDescription) &&
        cycleDetails.billingCycleDescription !== cycleDetails.billingCycleName;
    const runsOn =
        cycleDetails?.runDayOfMonth != null
            ? `${ordinal(cycleDetails.runDayOfMonth)} of the month`
            : null;
    const periodStart = dayOfMonthLabel(cycleDetails?.periodStartDayOfMonth);
    const weekday =
        cycleDetails?.dayOfWeek != null
            ? DAY_NAMES[Number(cycleDetails.dayOfWeek)] || `Day ${cycleDetails.dayOfWeek}`
            : null;
    const monthsAhead = Number(cycleDetails?.runMonthsAhead);
    const aheadLabel = Number.isFinite(monthsAhead) && monthsAhead > 0
        ? monthsAhead === 1
            ? "1 month ahead"
            : `${monthsAhead} months ahead`
        : null;

    return (
        <div className="container bill-runs">
            <p className="title">Bills</p>
            <p className="sub-title">
                Premium invoices produced by an HRP bill run. Paying a bill is not the same as
                funding claim payables.
            </p>

            <div className="bill-runs__filters">
                <div className="bill-runs__field bill-runs__field--account">
                    <label>Account</label>
                    <AsyncPaginate
                        className="react-select-container"
                        classNamePrefix="react-select"
                        value={account}
                        loadOptions={loadAccountHccIdentificationNumber}
                        onChange={onAccountChange}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{
                            ...customStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 200 }),
                        }}
                        isClearable
                    />
                </div>
                <div className="bill-runs__field bill-runs__field--date">
                    <label>As of</label>
                    <DatePicker
                        selected={asOfDate}
                        onChange={(d) => setAsOfDate(d || new Date())}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="As of date"
                        todayButton="Today"
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                        portalId="root"
                        popperPlacement="bottom-start"
                    />
                </div>
                <div className="bill-runs__field bill-runs__field--search">
                    <label>Search</label>
                    <div className="bill-runs__search-combo">
                        <input
                            type="text"
                            className="bill-runs__search"
                            placeholder="Bill #, account HCC, name, cycle"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") setSearchApplied(search.trim());
                            }}
                        />
                        <button
                            type="button"
                            className="bill-runs__search-btn"
                            onClick={() => setSearchApplied(search.trim())}
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            <div className="bill-runs__toolbar">
                <p
                    className="bill-runs__text-btn bill-runs__text-btn--danger"
                    onClick={() => {
                        setAccount(null);
                        setAsOfDate(new Date());
                        setSearch("");
                        setSearchApplied("");
                        setSearchParams({}, { replace: true });
                    }}
                >
                    Clear filters
                </p>
            </div>

            {accountKey || accountHccId ? (
                <section className="bill-runs__cycle" aria-label="Account billing cycle">
                    {cycleLoading ? (
                        <p className="muted">Loading billing cycle…</p>
                    ) : cycleError || cycle?.errorMessage ? (
                        <p className="muted">
                            {cycle?.errorMessage ||
                                cycleError?.message ||
                                "Could not load billing cycle"}
                        </p>
                    ) : (
                        <>
                            <div>
                                <span className="bill-runs__cycle-label">Billing cycle</span>
                                <strong>{cycleDetails?.billingCycleName || "—"}</strong>
                                {showCycleDesc ? <p>{cycleDetails.billingCycleDescription}</p> : null}
                            </div>
                            <div>
                                <span className="bill-runs__cycle-label">Schedule</span>
                                <strong>{scheduleLabel(cycleDetails?.schedule) || "—"}</strong>
                                {weekday ? <p>{weekday}</p> : null}
                                {aheadLabel ? <p>{aheadLabel}</p> : null}
                            </div>
                            {runsOn ? (
                                <div>
                                    <span className="bill-runs__cycle-label">Runs</span>
                                    <strong>{runsOn}</strong>
                                </div>
                            ) : null}
                            {periodStart ? (
                                <div>
                                    <span className="bill-runs__cycle-label">Period</span>
                                    <strong>{periodStart}</strong>
                                </div>
                            ) : null}
                            <div>
                                <span className="bill-runs__cycle-label">Status</span>
                                {cycleChip ? (
                                    <span
                                        className={`bill-runs__status bill-runs__status--${cycleChip.tone}`}
                                    >
                                        {cycleChip.label}
                                    </span>
                                ) : (
                                    <strong>—</strong>
                                )}
                            </div>
                        </>
                    )}
                </section>
            ) : null}

            <div className="bill-runs__toolbar bill-runs__toolbar--end">
                <p
                    className="bill-runs__text-btn"
                    onClick={() => setShowMoreColumns(!showMoreColumns)}
                >
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>

            {status === "error" ? (
                <span className="danger">Error: {error.message || "Failed to load bills"}</span>
            ) : (
                <SharedTable
                    type="bill-runs"
                    columns={columns}
                    items={items}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    noResultsMessage="No bills match the current filters."
                    ref={ref}
                    disableModel
                    onDetailHandler={async (row) => {
                        const key = row.billFactKey || row.key;
                        const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
                        navigate(`/finance/bills/${key}${qs}`);
                        return {};
                    }}
                />
            )}
        </div>
    );
};

export default BillRuns;
